import { formatCheckedOn } from "../../components/home/home-content"
import type { Guide } from "../guide-types"

// Copy source: docs/design/landing/copy.md section 2.11. Facts re-read on the ATO pages below on
// 2026-10-28 (logbook method page "Last updated 19 June 2024"; record-keeping page "Last updated
// 18 June 2026"). Re-open both and bump CHECKED when you edit.
const CHECKED = "2026-10-28"
const MOTOR =
  "https://www.ato.gov.au/businesses-and-organisations/income-deductions-and-concessions/income-and-deductions-for-business/deductions/deductions-for-motor-vehicle-expenses"
const LOGBOOK_URL = `${MOTOR}/logbook-method`
const RECORDS_URL =
  "https://www.ato.gov.au/businesses-and-organisations/preparing-lodging-and-paying/record-keeping-for-business/overview-of-record-keeping-rules-for-business"

const link =
  "focus-visible:ring-ring rounded-sm underline underline-offset-2 outline-none focus-visible:ring-2"

const H2 = ({ id, children }: { id: string; children: string }) => (
  <h2 id={id} className="text-2xl font-semibold">
    {children}
  </h2>
)

export const logbookRequirementsGuide: Guide = {
  slug: "ato-logbook-requirements",
  title: "ATO logbook requirements: the 12-week rules",
  description:
    "What the ATO asks for in a car logbook: 12 continuous weeks, odometer readings, a reason for each trip, and how long it stays valid.",
  crumb: "Logbook requirements",
  h1: "ATO logbook requirements, step by step",
  published: CHECKED,
  reviewed: CHECKED,
  financialYear: "2026–27",
  checked: formatCheckedOn(CHECKED),
  sources: [
    { label: "ATO, logbook method", href: LOGBOOK_URL },
    { label: "ATO, record-keeping rules for business", href: RECORDS_URL },
    { label: "ATO, cents per kilometre method", href: `${MOTOR}/cents-per-kilometre-method` },
  ],
  answer: (
    <>
      A logbook covers at least 12 continuous weeks that are representative of your travel through
      the year. Record the start and end of the period and the car&apos;s odometer readings at both
      ends. For each journey record the reason, the start and end dates, the odometer readings and
      the kilometres. A logbook is valid for five years, but you may need a new one if your
      circumstances change.
    </>
  ),
  related: [
    { label: "Mileage tracker: automatic GPS logbook", href: "/mileage-tracker" },
    { label: "Logbook method: when it pays off", href: "/logbook-method" },
    { label: "Free ATO logbook template", href: "/ato-logbook-template" },
  ],
  toc: [
    { id: "who", label: "Who needs a logbook" },
    { id: "what", label: "What goes in it" },
    { id: "weeks", label: "How to pick 12 weeks" },
    { id: "valid", label: "How long it lasts" },
    { id: "records", label: "Keep your records" },
    { id: "mistakes", label: "Easy-to-miss details" },
  ],
  body: (
    <>
      <section aria-labelledby="who" className="grid gap-3">
        <H2 id="who">Who needs a logbook</H2>
        <p>
          The logbook method is for sole traders and some partnerships claiming expenses for a car.
          You keep a logbook to work out your business-use percentage: business kilometres divided
          by total kilometres, times 100. You then apply that percentage to your total car expenses
          for the income year. If you claim cents per kilometre instead, you do not need a logbook.
        </p>
        <p>
          Not sure which method pays more? Compare both with your own numbers on{" "}
          <a href="/logbook-method" className={link}>
            Logbook method: when it pays off
          </a>
          .
        </p>
      </section>

      <section aria-labelledby="what" className="grid gap-3">
        <H2 id="what">What goes in it</H2>
        <p>The logbook as a whole must show:</p>
        <ul className="list-disc space-y-1 pl-6">
          <li>when the logbook period begins and ends</li>
          <li>the car&apos;s odometer readings at the start and end of the period</li>
          <li>the total kilometres the car travelled in the period</li>
          <li>
            the kilometres for each journey (several journeys in a row on the same day can be
            recorded as one)
          </li>
          <li>
            the odometer readings at the start and end of each later income year the logbook is
            valid for
          </li>
          <li>the business-use percentage for the period</li>
          <li>the make, model, engine capacity and registration number of the car</li>
        </ul>
        <p>For each journey, record:</p>
        <ul className="list-disc space-y-1 pl-6">
          <li>the reason (a description of the business reason, or that it was private use)</li>
          <li>the start and end date</li>
          <li>the odometer readings at the start and end</li>
          <li>the kilometres travelled</li>
        </ul>
        <p>
          Our{" "}
          <a href="/ato-logbook-template" className={link}>
            free logbook template
          </a>{" "}
          has a column for each of these. See also the glossary entry{" "}
          <a href="/glossary/logbook" className={link}>
            What is a logbook?
          </a>
        </p>
      </section>

      <section aria-labelledby="weeks" className="grid gap-3">
        <H2 id="weeks">How to pick 12 weeks</H2>
        <p>
          In the first year you use the logbook method, you must keep a logbook for at least 12
          continuous weeks during the income year. Those weeks need to be representative of your
          travel through the year, so avoid a stretch that is unusually quiet or busy.
        </p>
        <p>
          If you started using the car for business less than 12 weeks before the end of the income
          year, you can keep the logbook going into the next year to cover the 12 weeks. If you use
          the method for two or more cars, each car&apos;s logbook must cover the same period, and
          the weeks you choose should be representative of the business use of all the cars.
        </p>
      </section>

      <section aria-labelledby="valid" className="grid gap-3">
        <H2 id="valid">How long it lasts</H2>
        <p>
          Each logbook is valid for five years, and you may start a new one at any time. If you use
          a logbook from an earlier year to set your business-use percentage, you must keep that
          logbook and keep recording odometer readings in the later years. If your circumstances
          change, such as the type of work your business does, you may need a new logbook.
        </p>
      </section>

      <section aria-labelledby="records" className="grid gap-3">
        <H2 id="records">Keep your records</H2>
        <p>
          The ATO says you need to keep most business records for 5 years, generally from when you
          prepared or obtained the record or finished the transaction it relates to, whichever is
          later. For the logbook method that means the logbook and the evidence of your car
          expenses: your actual fuel and oil costs (or odometer readings you estimate them from) and
          your other car expenses.
        </p>
      </section>

      <section aria-labelledby="mistakes" className="grid gap-3">
        <H2 id="mistakes">Easy-to-miss details</H2>
        <ul className="list-disc space-y-1 pl-6">
          <li>
            A reason of just &ldquo;business&rdquo; says little. The ATO asks for a description of
            the business reason. Naming the client, site or job does that.
          </li>
          <li>
            Odometer readings are needed at the start and end of the period, not only per journey.
          </li>
          <li>
            Odometer readings are also needed at the start and end of each later income year the
            logbook stays in use.
          </li>
        </ul>
      </section>
    </>
  ),
}
